import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Mail, Server, Lock, User, Send, CheckCircle, 
  AlertCircle, Loader2, Eye, EyeOff, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EmailConfigPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const [config, setConfig] = useState({
    smtp_server: '',
    smtp_port: 587,
    smtp_security: 'TLS',
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: 'Vacu-Track',
    active: true
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/email/config`, { withCredentials: true });
      if (response.data.configured && response.data.config) {
        setConfig(prev => ({
          ...prev,
          ...response.data.config,
          smtp_password: '' // Don't show masked password in input
        }));
        setConfigured(true);
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validation
    if (!config.smtp_server || !config.smtp_user || !config.from_email) {
      toast.error('Complete los campos obligatorios');
      return;
    }
    if (!configured && !config.smtp_password) {
      toast.error('La contraseña es requerida');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...config };
      // If password is empty and already configured, don't send it (keep existing)
      if (configured && !payload.smtp_password) {
        delete payload.smtp_password;
      }

      await axios.post(`${API}/email/config`, payload, { withCredentials: true });
      toast.success('Configuración guardada correctamente');
      setConfigured(true);
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Ingrese un correo de destino');
      return;
    }

    setTesting(true);
    try {
      await axios.post(`${API}/email/test`, {
        destinatario: testEmail,
        asunto: 'Prueba de correo - Vacu-Track',
        mensaje: 'Este es un correo de prueba para verificar la configuración del servidor SMTP.'
      }, { withCredentials: true });
      
      toast.success(`Correo de prueba enviado a ${testEmail}`);
      setTestDialogOpen(false);
      setTestEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar correo de prueba');
    } finally {
      setTesting(false);
    }
  };

  const handleToggle = async (active) => {
    try {
      await axios.put(`${API}/email/toggle?active=${active}`, {}, { withCredentials: true });
      setConfig(prev => ({ ...prev, active }));
      toast.success(`Notificaciones ${active ? 'activadas' : 'desactivadas'}`);
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Configuración de Correo</h1>
            <p className="text-slate-500">Configure el servidor SMTP para enviar notificaciones por email</p>
          </div>
          <div className="flex items-center gap-4">
            {configured && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Notificaciones</span>
                <Switch
                  checked={config.active}
                  onCheckedChange={handleToggle}
                  data-testid="toggle-active"
                />
              </div>
            )}
          </div>
        </div>

        {/* Status Card */}
        <Card className={configured ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              {configured ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-medium">Correo configurado</span>
                  {config.active ? (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Activo</span>
                  ) : (
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Inactivo</span>
                  )}
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <span className="text-amber-700 font-medium">Configure el servidor SMTP para habilitar notificaciones por correo</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Configuration Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Servidor SMTP
            </CardTitle>
            <CardDescription>
              Ingrese los datos de su servidor de correo. Para cPanel, generalmente es mail.sudominio.com
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Server Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <Label htmlFor="smtp_server" className="flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  Servidor SMTP *
                </Label>
                <Input
                  id="smtp_server"
                  value={config.smtp_server}
                  onChange={(e) => handleChange('smtp_server', e.target.value)}
                  placeholder="mail.sudominio.com"
                  className="mt-1"
                  data-testid="input-smtp-server"
                />
                <p className="text-xs text-slate-500 mt-1">Ej: mail.extremebits.cl</p>
              </div>
              
              <div>
                <Label htmlFor="smtp_port">Puerto *</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  value={config.smtp_port}
                  onChange={(e) => handleChange('smtp_port', parseInt(e.target.value))}
                  placeholder="587"
                  className="mt-1"
                  data-testid="input-smtp-port"
                />
                <p className="text-xs text-slate-500 mt-1">587 (TLS) o 465 (SSL)</p>
              </div>
              
              <div>
                <Label htmlFor="smtp_security">Seguridad *</Label>
                <Select 
                  value={config.smtp_security} 
                  onValueChange={(value) => handleChange('smtp_security', value)}
                >
                  <SelectTrigger className="mt-1" data-testid="select-security">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TLS">TLS (Recomendado)</SelectItem>
                    <SelectItem value="SSL">SSL</SelectItem>
                    <SelectItem value="NONE">Sin encriptar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Authentication */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="smtp_user" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Usuario SMTP *
                </Label>
                <Input
                  id="smtp_user"
                  value={config.smtp_user}
                  onChange={(e) => handleChange('smtp_user', e.target.value)}
                  placeholder="notificaciones@sudominio.com"
                  className="mt-1"
                  data-testid="input-smtp-user"
                />
                <p className="text-xs text-slate-500 mt-1">Generalmente es el correo completo</p>
              </div>
              
              <div>
                <Label htmlFor="smtp_password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Contraseña SMTP {!configured && '*'}
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="smtp_password"
                    type={showPassword ? 'text' : 'password'}
                    value={config.smtp_password}
                    onChange={(e) => handleChange('smtp_password', e.target.value)}
                    placeholder={configured ? '••••••••' : 'Contraseña del correo'}
                    className="pr-10"
                    data-testid="input-smtp-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {configured && (
                  <p className="text-xs text-slate-500 mt-1">Dejar vacío para mantener la contraseña actual</p>
                )}
              </div>
            </div>

            {/* From Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="from_email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Correo Remitente *
                </Label>
                <Input
                  id="from_email"
                  type="email"
                  value={config.from_email}
                  onChange={(e) => handleChange('from_email', e.target.value)}
                  placeholder="notificaciones@sudominio.com"
                  className="mt-1"
                  data-testid="input-from-email"
                />
                <p className="text-xs text-slate-500 mt-1">Dirección que verán los destinatarios</p>
              </div>
              
              <div>
                <Label htmlFor="from_name">Nombre Remitente</Label>
                <Input
                  id="from_name"
                  value={config.from_name}
                  onChange={(e) => handleChange('from_name', e.target.value)}
                  placeholder="Vacu-Track"
                  className="mt-1"
                  data-testid="input-from-name"
                />
                <p className="text-xs text-slate-500 mt-1">Nombre que aparecerá en el correo</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="gap-2"
                data-testid="btn-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
              
              {configured && (
                <Button 
                  variant="outline" 
                  onClick={() => setTestDialogOpen(true)}
                  className="gap-2"
                  data-testid="btn-test"
                >
                  <Send className="w-4 h-4" />
                  Enviar Correo de Prueba
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <h3 className="font-semibold text-blue-800 mb-2">Configuración típica para cPanel:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li><strong>Servidor:</strong> mail.sudominio.com</li>
              <li><strong>Puerto:</strong> 587 (TLS) o 465 (SSL)</li>
              <li><strong>Usuario:</strong> El correo completo (ej: notificaciones@sudominio.com)</li>
              <li><strong>Seguridad:</strong> TLS (recomendado) o SSL</li>
            </ul>
          </CardContent>
        </Card>

        {/* Test Email Dialog */}
        <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Enviar Correo de Prueba
              </DialogTitle>
              <DialogDescription>
                Se enviará un correo de prueba para verificar la configuración
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Label htmlFor="test-email">Correo de destino</Label>
              <Input
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="ejemplo@correo.com"
                className="mt-1"
                data-testid="input-test-email"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleTest} disabled={testing} className="gap-2">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {testing ? 'Enviando...' : 'Enviar Prueba'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EmailConfigPage;
